import React, { useEffect, useState } from 'react';
import { Box, Popover, Slider, Typography } from '@mui/material';
import { PRES, TYPE } from '../chrome';
import SteppedNumberField from './SteppedNumberField';

export function isToolbarPointerTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.closest) return false;
  return Boolean(el.closest(
    'input, textarea, select, [role="slider"], [role="combobox"], [role="listbox"], [role="option"], .MuiSelect-root, .MuiSelect-select, .MuiMenuItem-root, .MuiSlider-root, .MuiSlider-thumb, .MuiSlider-rail, .MuiSlider-track'
  ));
}

interface ToolbarFlyoutProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  width?: number;
  children: React.ReactNode;
}

export const ToolbarFlyout: React.FC<ToolbarFlyoutProps> = ({
  open, anchorEl, onClose, width = 300, children,
}) => (
  <Popover
    open={open && Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    slotProps={{
      paper: {
        elevation: 0,
        onMouseDown: (event: React.MouseEvent) => {
          if (isToolbarPointerTarget(event.target)) return;
          event.preventDefault();
        },
        sx: {
          mt: 1,
          px: 2,
          py: 1.75,
          width,
          borderRadius: 3,
          bgcolor: PRES.white,
          border: '1px solid rgba(10,39,87,0.08)',
          boxShadow: '0 16px 40px rgba(10,39,87,0.16)',
        },
      },
    }}
  >
    {children}
  </Popover>
);

interface SliderRowProps {
  label?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  disabled?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  startTitle?: string;
  endTitle?: string;
  onChange: (value: number) => void;
}

export const SliderRow: React.FC<SliderRowProps> = ({
  label, value, min, max, step = 1, disabled, startIcon, endIcon, startTitle, endTitle, onChange,
}) => {
  const [dragging, setDragging] = useState(false);
  const [live, setLive] = useState(value);

  useEffect(() => {
    if (!dragging) setLive(value);
  }, [value, dragging]);

  const emit = (next: number) => {
    setLive(next);
    onChange(next);
  };

  return (
    <Box>
    {label && (
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.label, color: PRES.textSecondary, mb: 0.75 }}>
        {label}
      </Typography>
    )}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {startIcon && (
        <Box title={startTitle} sx={{ color: PRES.textSecondary, display: 'flex', opacity: 0.7, flexShrink: 0 }}>
          {startIcon}
        </Box>
      )}
      <Slider
        size="small"
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        value={Math.min(max, Math.max(min, live))}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => {
          event.stopPropagation();
          setDragging(true);
        }}
        onChange={(_event, next) => {
          setDragging(true);
          emit(Array.isArray(next) ? next[0] : next);
        }}
        onChangeCommitted={() => setDragging(false)}
        sx={{
          flex: 1,
          color: PRES.interactive,
          py: 0.5,
          '& .MuiSlider-rail': {
            height: 6,
            borderRadius: 99,
            bgcolor: PRES.borderSubtle,
            opacity: 1,
          },
          '& .MuiSlider-track': {
            height: 6,
            border: 'none',
            borderRadius: 99,
            bgcolor: PRES.interactive,
          },
          '& .MuiSlider-thumb': {
            width: 18,
            height: 18,
            bgcolor: PRES.white,
            border: `2px solid ${PRES.interactive}`,
            boxShadow: '0 1px 6px rgba(20,22,28,0.18)',
            '&:hover, &.Mui-focusVisible, &.Mui-active': {
              boxShadow: `0 0 0 4px ${PRES.surface}, 0 0 0 6px ${PRES.focusRing}`,
            },
          },
        }}
      />
      {endIcon && (
        <Box title={endTitle} sx={{ color: PRES.textSecondary, display: 'flex', opacity: 0.7, flexShrink: 0 }}>
          {endIcon}
        </Box>
      )}
      <SteppedNumberField
        compact
        disabled={disabled}
        value={live}
        min={min}
        max={max}
        step={step}
        onChange={emit}
        width={58}
      />
    </Box>
  </Box>
  );
};

export const TrackingTightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
    <text x="3" y="14" fontSize="12" fontWeight="700" fill="currentColor">A</text>
    <text x="9" y="14" fontSize="12" fontWeight="700" fill="currentColor">V</text>
  </svg>
);

export const TrackingLooseIcon = () => (
  <svg width="22" height="18" viewBox="0 0 22 18" aria-hidden>
    <text x="1" y="14" fontSize="12" fontWeight="700" fill="currentColor">A</text>
    <text x="13" y="14" fontSize="12" fontWeight="700" fill="currentColor">V</text>
  </svg>
);

export const LeadingTightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
    <rect x="2" y="6" width="14" height="2" rx="1" fill="currentColor" />
    <rect x="2" y="10" width="14" height="2" rx="1" fill="currentColor" />
  </svg>
);

export const LeadingLooseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
    <rect x="2" y="3" width="14" height="2" rx="1" fill="currentColor" />
    <rect x="2" y="13" width="14" height="2" rx="1" fill="currentColor" />
  </svg>
);

export const flyoutTriggerSx = (active: boolean) => ({
  color: PRES.textPrimary,
  bgcolor: active ? PRES.white : 'transparent',
  boxShadow: active ? `0 0 0 1px ${PRES.borderSubtle}` : 'none',
  borderRadius: 1.5,
  '&:hover': { bgcolor: active ? PRES.white : PRES.surfaceSunken },
});
